import cx from 'classnames';
import { PageContent } from 'restaurantAdmin/layout/PageContent';
import { PageHeader } from 'restaurantAdmin/layout/PageHeader';
import styles from './MessagingTemplatePage.scss';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { OPERATIONS_MESSAGING_PATH } from 'restaurantAdmin/paths';
import {
  getMessagingTemplates,
  type MessagingTemplate,
  updateMessagingTemplate,
} from './apiHelpers';
import { Button, ButtonVariants } from '@components/button/Button';
import { useForm } from 'react-hook-form';
import { errorToast, successToast } from '@components/toasts/Toasts';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LoadingPage } from '@components/LoadingPage';
import { reportAppError } from '@shared/reportAppError';
import { Tab } from '@mui/base/Tab';
import { TabsList } from '@components/tabs/TabsList';
import { Tabs } from '@mui/base/Tabs';

export const MessagingTemplatePage = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const { id: restaurantId } = useRestaurant();
  const queryClient = useQueryClient();

  const { data: templates, isLoading } = useQuery({
    queryKey: ['messagingTemplates', restaurantId],
    queryFn: () => getMessagingTemplates(restaurantId),
  });

  const form = useForm<MessagingTemplate>({
    defaultValues: {
      additionalContent: '',
    },
  });

  const [selectedTemplate, setSelectedTemplate] =
    useState<MessagingTemplate | null>(null);

  useEffect(() => {
    const currentTemplate = templates?.find((t) => t.id === templateId);
    setSelectedTemplate(currentTemplate || null);
    form.reset(currentTemplate);
  }, [templateId, templates]);

  const updateMessagingTemplateMutation = useMutation({
    mutationFn: async () => {
      return updateMessagingTemplate(restaurantId, templateId, {
        additionalContent: form.getValues().additionalContent || null,
      });
    },
    onSuccess: () => {
      successToast({ message: 'Template updated successfully' });
      queryClient.invalidateQueries({
        queryKey: ['messagingTemplates', restaurantId],
      });
    },
    onError: (e) => {
      errorToast({ message: 'Template could not be updated' });
      reportAppError(e);
    },
  });

  if (isLoading) {
    return <LoadingPage />;
  }

  if (!templates || templates.length === 0 || !selectedTemplate) {
    return (
      <>
        <PageHeader
          title="Message Templates"
          category="Operations"
        ></PageHeader>
        <PageContent>
          <p>No templates found. Try refreshing the page.</p>
        </PageContent>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Message Templates" category="Operations"></PageHeader>
      <PageContent className={styles.container}>
        <aside className={styles.sidebar}>
          <ul aria-label="Messaging Templates">
            {templates.map((template) => (
              <li
                key={template.id}
                className={cx({
                  [styles.active]: template.id === templateId,
                })}
              >
                <Link
                  className={styles.sidebarLink}
                  to={`${OPERATIONS_MESSAGING_PATH}/${template.id}`}
                >
                  {template.name}
                </Link>
              </li>
            ))}
          </ul>
        </aside>
        <section className={styles.main}>
          <header className={styles.header}>
            <h2 className={styles.title}>{selectedTemplate?.name}</h2>
            <p className={styles.description}>
              {selectedTemplate?.description}
            </p>
          </header>

          <Tabs defaultValue={0} className={styles.tabs}>
            <TabsList className={styles.tabList}>
              <Tab value={0}>SMS</Tab>
            </TabsList>
          </Tabs>

          <form
            onSubmit={form.handleSubmit(() =>
              updateMessagingTemplateMutation.mutate(),
            )}
          >
            <div className={styles.mainContent}>
              <div className={styles.mainContentItem}>
                <label htmlFor="template">Standard Message</label>
                <textarea
                  id="template"
                  disabled
                  value={selectedTemplate?.template}
                />
                <p className={styles.mainContentSubtext}>
                  Standard Message text cannot be changed.
                </p>
              </div>

              <div className={styles.mainContentItem}>
                <label htmlFor="additionalContent">
                  Additional Text (optional, to be added to the Standard
                  Message)
                </label>
                <textarea
                  id="additionalContent"
                  {...form.register('additionalContent')}
                />
              </div>
            </div>

            <footer className={styles.footer}>
              <Button
                variant={ButtonVariants.Primary}
                label="Save Template"
                type="submit"
              />
            </footer>
          </form>
        </section>
      </PageContent>
    </>
  );
};
